/**
 * WarehouseSummaryPanel
 * /api/warehouse/analytics/inventory_summary/
 * Shows: total items, total value, low-stock count, out-of-stock count + category breakdown
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Package, DollarSign, AlertTriangle, XCircle, RefreshCw, Loader2, Warehouse } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

const fmt = n => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtVal = n => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}k`;
  return v.toFixed(0);
};

const CAT_COLORS = ['#3B82F6','#10B981','#F59E0B','#8B5CF6','#EC4899','#14B8A6','#F97316','#EF4444'];

const Metric = ({ icon: Icon, label, value, color, sub }) => (
  <div className="flex items-start gap-3 p-3 rounded-xl bg-steel-50 border border-steel-100">
    <div className="p-2 rounded-lg flex-shrink-0" style={{ backgroundColor: `${color}18` }}>
      <Icon size={16} style={{ color }} />
    </div>
    <div className="min-w-0">
      <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wide leading-none mb-0.5">{label}</p>
      <p className="text-lg font-bold text-slate-800 leading-none">{value}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const WarehouseSummaryPanel = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = () => {
    setLoading(true); setError(null);
    const headers = { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
    axios.get(`${API_BASE}/api/warehouse/analytics/inventory_summary/`, { headers })
      .then(r => setData(r.data))
      .catch(() => setError('Could not load warehouse data.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line

  return (
    <div className="h-full flex flex-col bg-white overflow-hidden">
      {/* header */}
      <div className="flex items-center justify-between px-3 py-2 border-b-2 border-brand-500 bg-steel-50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded-lg bg-gradient-to-br from-brand-500 to-brand-600">
            <Warehouse size={13} className="text-white" />
          </div>
          <span className="text-xs font-semibold text-slate-800">Warehouse Overview</span>
        </div>
        <button onClick={load} className="p-1 rounded hover:bg-steel-200 text-slate-400 hover:text-slate-600 transition-colors">
          <RefreshCw size={12} />
        </button>
      </div>

      {loading && <div className="flex-1 flex items-center justify-center"><Loader2 size={20} className="animate-spin text-slate-300" /></div>}
      {!loading && error && <div className="flex-1 flex items-center justify-center text-xs text-rose-400 px-4 text-center">{error}</div>}

      {!loading && !error && data && (
        <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
          {/* 4 KPI metrics */}
          <div className="grid grid-cols-2 gap-2">
            <Metric icon={Package}       label="Total Items"   value={fmt(data.total_items)}       color="#3B82F6" />
            <Metric icon={DollarSign}    label="Total Value"   value={fmtVal(data.total_value)}    color="#10B981" sub="inventory value" />
            <Metric icon={AlertTriangle} label="Low Stock"     value={fmt(data.low_stock_items)}   color="#F59E0B" sub="need restocking" />
            <Metric icon={XCircle}       label="Out of Stock"  value={fmt(data.out_of_stock_items)} color="#EF4444" sub="empty items" />
          </div>

          {/* Category breakdown */}
          {data.categories_breakdown?.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Categories</p>
              <div className="space-y-1.5">
                {[...data.categories_breakdown]
                  .sort((a, b) => (b.item_count || 0) - (a.item_count || 0))
                  .slice(0, 6)
                  .map((cat, i, arr) => {
                    const maxCount = arr[0]?.item_count || 1;
                    return (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: CAT_COLORS[i % CAT_COLORS.length] }} />
                        <span className="text-[11px] text-slate-600 truncate flex-1 min-w-0">{cat.name}</span>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <div className="w-16 h-1.5 bg-steel-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{
                              width: `${Math.round((cat.item_count / maxCount) * 100)}%`,
                              backgroundColor: CAT_COLORS[i % CAT_COLORS.length],
                            }} />
                          </div>
                          <span className="text-[10px] text-slate-500 font-medium w-8 text-right">{cat.item_count ?? 0}</span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WarehouseSummaryPanel;
